import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { SpinAWheelApi, SpinWheelConfig } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";

const spinWheelFormSchema = z.object({
  spinWheelName: z.string().min(1, "Spin Wheel Name is required"),
  startDate: z.date({
    required_error: "Start Date is required",
  }),
  endDate: z.date({
    required_error: "End Date is required",
  }),
  noOfSlots: z.string().min(1, "Number of Slots is required"),
  spinPerMember: z.string().min(1, "Spin per Member is required"),
  bonusValidityDays: z.string().min(1, "Bonus Validity in Days is required"),
  useDefault: z.boolean().default(false),
  slots: z.array(
    z.object({
      name: z.string().min(1, "Slot name is required"),
      points: z.string().min(1, "Points is required"),
    })
  ),
}).refine((data) => {
  if (data.endDate && data.startDate) {
    return data.endDate >= data.startDate;
  }
  return true;
}, {
  message: "End Date must be after Start Date",
  path: ["endDate"],
});

type SpinWheelFormValues = z.infer<typeof spinWheelFormSchema>;

interface CreateSpinWheelFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  initialData?: SpinWheelConfig | null;
  isEditMode?: boolean;
}

export function CreateSpinWheelForm({
  onSuccess,
  onCancel,
  initialData,
  isEditMode = false,
}: CreateSpinWheelFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  // Map API slots to form slots format
  const mapSlotsToForm = (slots?: any[]): Array<{ name: string; points: string }> => {
    if (!slots || slots.length === 0) return [];
    return slots.map((slot) => ({
      name: slot.slotName || slot.name || "",
      points: (slot.points || 0).toString(),
    }));
  };

  const form = useForm<SpinWheelFormValues>({
    resolver: zodResolver(spinWheelFormSchema),
    defaultValues: {
      spinWheelName: initialData?.spinName || "",
      startDate: initialData?.startDate ? new Date(initialData.startDate) : undefined,
      endDate: initialData?.endDate ? new Date(initialData.endDate) : undefined,
      noOfSlots: initialData?.noOfSlots?.toString() || "",
      spinPerMember: initialData?.spinPerMember ? `${initialData.spinPerMember} per day` : "",
      bonusValidityDays: initialData?.bonusValidityInDay?.toString() || "",
      useDefault: initialData?.useDefault === 1,
      slots: initialData?.spinAWheelSlots 
        ? mapSlotsToForm(initialData.spinAWheelSlots)
        : initialData?.listWheelSlots
        ? mapSlotsToForm(initialData.listWheelSlots)
        : [],
    },
  });

  const noOfSlots = form.watch("noOfSlots");

  // Ensure "Use Default" reflects API when editing (in case initialData arrives after mount)
  useEffect(() => {
    if (isEditMode && initialData) {
      form.setValue("useDefault", initialData.useDefault === 1);
    }
  }, [isEditMode, initialData, form]);

  // Update slots array when noOfSlots changes (only if not in edit mode or slots are empty)
  useEffect(() => {
    if (noOfSlots && !isNaN(Number(noOfSlots))) {
      const count = Number(noOfSlots);
      const currentSlots = form.getValues("slots");
      
      // Only update if slots array length doesn't match or if we're not in edit mode
      if (currentSlots.length !== count || !isEditMode) {
      const newSlots = Array.from({ length: count }, (_, index) => ({
        name: currentSlots[index]?.name || "",
        points: currentSlots[index]?.points || "",
      }));
      form.setValue("slots", newSlots);
      }
    } else if (!noOfSlots) {
      form.setValue("slots", []);
    }
  }, [noOfSlots, form, isEditMode]);

  const onSubmit = async (data: SpinWheelFormValues) => {
    setIsSubmitting(true);
    try {
      // Extract number from "1 per day" format for p_SpinPerMember
      const spinPerMemberMatch = data.spinPerMember.match(/\d+/);
      const spinPerMemberNumber = spinPerMemberMatch
        ? Number(spinPerMemberMatch[0])
        : 1;

      const payload = {
        p_Id: isEditMode && initialData ? initialData.id : 0,
        p_SpinName: data.spinWheelName,
        p_StartDate: data.startDate.toISOString(),
        p_EndDate: data.endDate.toISOString(),
        p_NoOfSlots: Number(data.noOfSlots),
        p_SpinPerMember: spinPerMemberNumber,
        p_BonusValidityInDay: Number(data.bonusValidityDays),
        p_NonWinningSlots: initialData?.nonWinningSlots || 0,
        p_ClientId: 1009,
        p_UseDefault: data.useDefault,
        listWheelSlots: data.slots.map((slot) => ({
          slotName: slot.name,
          points: Number(slot.points),
        })),
      };

      const response = await SpinAWheelApi.createUpdateSpinAWheel(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: response.message || (isEditMode ? "Spin Wheel updated successfully" : "Spin Wheel created successfully"),
        });
        form.reset();
        onSuccess?.();
      }
    } catch (error: any) {
      console.error("Error creating spin wheel:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to create spin wheel",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="spinWheelName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Spin Wheel Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter spin wheel name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Start Date</FormLabel>
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setStartDateOpen(false);
                      }}
                      disabled={(date) => date < new Date("1900-01-01")}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>End Date</FormLabel>
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setEndDateOpen(false);
                      }}
                      disabled={(date) => {
                        const startDate = form.getValues("startDate");
                        return (
                          date < new Date("1900-01-01") ||
                          (startDate && date < startDate)
                        );
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

        </div>

        <FormField
          control={form.control}
          name="noOfSlots"
          render={({ field }) => (
            <FormItem>
              <FormLabel>No of Slots</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select number of slots" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {[3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {noOfSlots && Number(noOfSlots) > 0 && (
          <div className="space-y-4">
            <FormLabel>Slot Details</FormLabel>
            {form.watch("slots").map((_, index) => (
              <div key={index} className="grid grid-cols-2 gap-4 p-4 border rounded-md">
                <FormField
                  control={form.control}
                  name={`slots.${index}.name`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Slot {index + 1} Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter slot name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`slots.${index}.points`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Points</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter points"
                          min="0"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}
          </div>
        )}

        <FormField
          control={form.control}
          name="spinPerMember"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Spin per Member</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select spin per member" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="1 per day">1 per day</SelectItem>
                  <SelectItem value="2 per day">2 per day</SelectItem>
                  <SelectItem value="3 per day">3 per day</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bonusValidityDays"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bonus Validity in Days</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Enter bonus validity in days"
                  min="1"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="useDefault"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Use Default</FormLabel>
                <p className="text-sm text-muted-foreground">
                  Use default settings for this spin wheel.
                </p>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="bg-orange-600 text-white hover:bg-orange-700"
          >
            {isSubmitting
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
              ? "Update"
              : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
